/**
 * Notification Bell — shared ops inbox peek + sound mute toggle.
 * Uses dropdownRender so the panel toggles closed on second bell click.
 */

import React, { useEffect, useState } from "react";
import {
  Badge,
  Dropdown,
  Button,
  List,
  Space,
  Typography,
  Divider,
  Tag,
  Tooltip,
} from "antd";
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  LoadingOutlined,
  SoundOutlined,
  MutedOutlined,
} from "@ant-design/icons";
import { useNotifications } from "../context/NotificationContext.jsx";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  getCtaLabel,
  getNotificationPath,
  isActionRequired,
} from "../utils/taxonomy.js";
import {
  isNotificationSoundMuted,
  setNotificationSoundMuted,
  unlockNotificationSound,
} from "../utils/notificationSound.js";

dayjs.extend(relativeTime);

const { Text } = Typography;

const MOBILE_BREAKPOINT = "(max-width: 767px)";

const NotificationBell = () => {
  const {
    notifications,
    stats,
    loading,
    markAsRead,
    deleteNotification,
    markAllAsRead,
    deleteAllNotifications,
  } = useNotifications();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [soundMuted, setSoundMuted] = useState(() =>
    isNotificationSoundMuted()
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(MOBILE_BREAKPOINT).matches
      : false
  );
  const navigate = useNavigate();

  const unreadCount = stats?.unread || 0;
  const actionRequired =
    stats?.actionRequired ?? stats?.byCategory?.action_required ?? 0;
  const totalCount = stats?.total || 0;

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_BREAKPOINT);
    const onChange = (event) => {
      setIsMobile(event.matches);
      if (event.matches) {
        setDropdownVisible(false);
      }
    };

    setIsMobile(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const openNotificationsPage = () => {
    unlockNotificationSound();
    setDropdownVisible(false);
    navigate(
      actionRequired > 0
        ? "/notifications?category=action_required"
        : "/notifications"
    );
  };

  const handleOpenChange = (nextOpen) => {
    if (isMobile) {
      setDropdownVisible(false);
      return;
    }
    // Unlock audio on the same gesture that opens the bell
    if (nextOpen) {
      unlockNotificationSound();
    }
    setDropdownVisible(nextOpen);
  };

  const toggleSoundMute = (e) => {
    e?.stopPropagation?.();
    const next = !soundMuted;
    setNotificationSoundMuted(next);
    setSoundMuted(next);
    if (!next) {
      unlockNotificationSound();
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    const path = getNotificationPath(notification);
    if (path) {
      navigate(path);
    } else {
      navigate("/notifications");
    }

    setDropdownVisible(false);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "#ff4d4f";
      case "high":
        return "#fa8c16";
      case "medium":
        return "#1890ff";
      case "low":
        return "#52c41a";
      default:
        return "#1890ff";
    }
  };

  const renderNotificationItem = (notification) => (
    <List.Item
      key={notification.id}
      style={{
        padding: "16px",
        cursor: "pointer",
        backgroundColor: notification.isRead ? "#fafafa" : "#fff",
        borderBottom: "1px solid #f0f0f0",
        transition: "background-color 0.2s ease",
      }}
      onClick={() => handleNotificationClick(notification)}
      actions={[
        <Button
          key="read"
          type="text"
          size="small"
          icon={<CheckOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            markAsRead(notification.id);
          }}
          disabled={notification.isRead}
        />,
        <Button
          key="delete"
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            deleteNotification(notification.id);
          }}
        />,
      ]}
    >
      <List.Item.Meta
        avatar={
          <div
            style={{
              fontSize: "20px",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: getPriorityColor(notification.priority),
              color: "white",
            }}
          >
            {isActionRequired(notification) ? "!" : "•"}
          </div>
        }
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Text
              strong={!notification.isRead}
              style={{
                color: notification.isRead ? "#666" : "#000",
                fontSize: "14px",
              }}
            >
              {notification.title}
            </Text>
            {!notification.isRead && (
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#1890ff",
                }}
              />
            )}
            {isActionRequired(notification) && (
              <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>
                Action
              </Tag>
            )}
          </div>
        }
        description={
          <div>
            <Text
              style={{
                color: notification.isRead ? "#999" : "#666",
                fontSize: "12px",
              }}
            >
              {notification.message}
            </Text>
            <br />
            <Text style={{ color: "#999", fontSize: "11px" }}>
              {dayjs(notification.created_at).fromNow()} ·{" "}
              {getCtaLabel(notification)}
            </Text>
          </div>
        }
      />
    </List.Item>
  );

  const dropdownContent = (
    <div
      style={{
        width: "400px",
        maxHeight: "500px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        border: "1px solid #f0f0f0",
      }}
    >
      <div
        style={{
          padding: "16px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fafafa",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
        }}
      >
        <div>
          <Text strong>Ops inbox</Text>
          {actionRequired > 0 && (
            <div>
              <Text type="warning" style={{ fontSize: 12 }}>
                {actionRequired} action required
              </Text>
            </div>
          )}
        </div>
        <Space>
          <Tooltip
            title={
              soundMuted
                ? "Unmute new-notification sound"
                : "Mute new-notification sound"
            }
          >
            <Button
              type="text"
              size="small"
              icon={soundMuted ? <MutedOutlined /> : <SoundOutlined />}
              onClick={toggleSoundMute}
              aria-label={soundMuted ? "Unmute notification sound" : "Mute notification sound"}
            />
          </Tooltip>
          <Button
            type="text"
            size="small"
            onClick={markAllAsRead}
            disabled={stats?.unread === 0}
          >
            Mark all read
          </Button>
          <Button
            type="text"
            size="small"
            danger
            onClick={deleteAllNotifications}
            disabled={notifications.length === 0}
          >
            Clear all
          </Button>
        </Space>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        {loading ? (
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "#999",
            }}
          >
            <LoadingOutlined
              style={{ fontSize: "24px", marginBottom: "8px" }}
            />
            <br />
            Loading notifications...
          </div>
        ) : notifications.length === 0 ? (
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "#999",
            }}
          >
            <BellOutlined style={{ fontSize: "24px", marginBottom: "8px" }} />
            <br />
            No notifications
          </div>
        ) : (
          <List
            dataSource={notifications.slice(0, 10)}
            renderItem={renderNotificationItem}
            style={{ padding: 0 }}
          />
        )}
      </div>

      <Divider style={{ margin: "8px 0" }} />
      <div
        style={{
          padding: "12px 16px",
          textAlign: "center",
          backgroundColor: "#fafafa",
          borderBottomLeftRadius: "8px",
          borderBottomRightRadius: "8px",
        }}
      >
        <Button type="link" size="small" onClick={openNotificationsPage}>
          View shared inbox ({totalCount})
        </Button>
      </div>
    </div>
  );

  const bellButton = (
    <Button
      type="text"
      aria-label="Notifications"
      icon={
        <Badge count={unreadCount} size="small" showZero={false}>
          <BellOutlined style={{ fontSize: "18px" }} />
        </Badge>
      }
      style={{
        height: "40px",
        width: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "8px",
        transition: "all 0.2s ease",
      }}
      onClick={isMobile ? openNotificationsPage : undefined}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "#f0f8ff";
        e.currentTarget.style.transform = "scale(1.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
        e.currentTarget.style.transform = "scale(1)";
      }}
    />
  );

  if (isMobile) {
    return bellButton;
  }

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={["click"]}
      open={dropdownVisible}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
    >
      {bellButton}
    </Dropdown>
  );
};

export default NotificationBell;
